// "Calendario de cierres" — ported from mantis-business-front-brinks's routes/calendario.tsx
// onto real data: `filterOffices` (the same BR-23 panel projection PanelCierres.jsx already
// uses) instead of the mockup's static `mock-data.ts`, and the real `CierreDetailDialog`
// (fetches `getCierreClosingDetail` by closingId) instead of brinks' own dialog, which is handed
// a whole mock `Cierre` object directly since it never fetches anything.
//
// The month/date-math helpers (`construirMes`, `desplazarMes`, `nombreDelMes`, `fechaLegible`)
// are pure calendar arithmetic with nothing client-specific in them, so they're carried over
// unchanged from brinks' file.
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { CalendarDays, ChevronLeft, ChevronRight, Download, Search, X } from "lucide-react";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
  toCsv,
  downloadCsv,
} from "@mantis-sas/ui";

import { selectAuthState } from "../../../store/selectors/authSelectors";
import { filterOffices } from "../../../ApiCalls/mantisBusiness";
import { estadoDeFila, fechaDMY, nombreOficinaDeMainKey, ETIQUETA_ESTADO } from "../../../utils/cierrePanel";
import { CierreDetailDialog } from "./CierreDetailDialog";
import { SabadosLaborales } from "./SabadosLaborales";
import { useClienteCierres } from "./ClienteCierresContext";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import { PERMISO, filasExportables, puede } from "../../../utils/cierrePermisos";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";

const ESTADOS = ["cuadre", "pendiente", "bloqueado"];
const PUNTO_CLASES = { cuadre: "bg-brinks-success", pendiente: "bg-brinks-warning", bloqueado: "bg-brinks-danger" };
const FILA_CLASES = {
  cuadre: "border-brinks-success-border bg-brinks-success-soft text-brinks-success",
  pendiente: "border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning",
  bloqueado: "border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger",
};
const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function claveMes({ anio, mes }) {
  return `${anio}-${String(mes + 1).padStart(2, "0")}`;
}

function desplazarMes(anio, mes, cantidad) {
  const fecha = new Date(Date.UTC(anio, mes + cantidad, 1));
  return { anio: fecha.getUTCFullYear(), mes: fecha.getUTCMonth() };
}

function nombreDelMes({ anio, mes }) {
  const nombre = new Date(Date.UTC(anio, mes, 1)).toLocaleDateString("es-CO", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  return nombre.charAt(0).toUpperCase() + nombre.slice(1);
}

function fechaLegible(fecha) {
  return new Date(`${fecha}T00:00:00Z`).toLocaleDateString("es-CO", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

function construirMes({ anio, mes }) {
  const primerDia = new Date(Date.UTC(anio, mes, 1)).getUTCDay();
  const offset = (primerDia + 6) % 7;
  const cantidad = new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();
  const anterior = desplazarMes(anio, mes, -1);
  const diasAnterior = new Date(Date.UTC(anterior.anio, anterior.mes + 1, 0)).getUTCDate();

  return Array.from({ length: 42 }, (_, indice) => {
    const relativo = indice - offset + 1;
    if (relativo < 1) return { dia: diasAnterior + relativo, pertenece: false, clave: "" };
    if (relativo > cantidad) return { dia: relativo - cantidad, pertenece: false, clave: "" };
    return { dia: relativo, pertenece: true, clave: `${claveMes({ anio, mes })}-${String(relativo).padStart(2, "0")}` };
  });
}

function filaDeCierre(row) {
  return {
    id: row.closing_id || `${row.codigo_oficina}-${row.fecha_cuadre}`,
    closingId: row.closing_id || null,
    codigo: row.codigo_oficina || "—",
    oficinaId: row.codigo_oficina || "—",
    oficina: nombreOficinaDeMainKey(row.main_key),
    fecha: row.fecha_cuadre || "",
    diferencia: row.diferencia_cuadre != null && row.diferencia_cuadre !== "" ? Number(row.diferencia_cuadre) : null,
    estado: estadoDeFila(row),
    raw: row,
  };
}

function MiniCalendario({ vista, fechasActivas, seleccion, onSeleccionar }) {
  return (
    <div>
      <div className="mb-2 grid grid-cols-7 text-center text-[9px] font-semibold uppercase text-brinks-muted-foreground">
        {DIAS.map((dia) => (
          <span key={dia}>{dia.slice(0, 1)}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5 text-center">
        {construirMes(vista).map((celda, indice) =>
          celda.pertenece ? (
            <Button
              key={`${celda.dia}-${indice}`}
              variant="ghost"
              size="icon"
              onClick={() => onSeleccionar(celda.clave)}
              className={cn(
                "relative mx-auto h-6 w-6 rounded-[6px] p-0 text-[10px] font-normal tabular-nums",
                celda.clave === seleccion && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
              )}
            >
              {celda.dia}
              {fechasActivas.has(celda.clave) && celda.clave !== seleccion ? (
                <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-brinks-foreground" />
              ) : null}
            </Button>
          ) : (
            <span key={`${celda.dia}-${indice}`} className="mx-auto grid h-6 w-6 place-items-center text-[10px] text-brinks-muted-foreground/35">
              {celda.dia}
            </span>
          )
        )}
      </div>
    </div>
  );
}

function ResumenEstados({ lista }) {
  return (
    <div className="space-y-1">
      {ESTADOS.map((estado) => (
        <div key={estado} className="flex items-center justify-between gap-3 rounded-[7px] px-2 py-1.5 hover:bg-brinks-surface">
          <span className="flex min-w-0 items-center gap-2 text-[11px] text-brinks-foreground">
            <span className={cn("h-2 w-2 shrink-0 rounded-full", PUNTO_CLASES[estado])} />
            <span className="truncate">{ETIQUETA_ESTADO[estado]}</span>
          </span>
          <span className="font-semibold tabular-nums text-brinks-foreground">{lista.filter((c) => c.estado === estado).length}</span>
        </div>
      ))}
    </div>
  );
}

function CierresCalendario(props) {
  // B-55 — which client the shell's selector is on. Every row below is that client's.
  const { clientCode, listo } = useClienteCierres();
  // BR-38 — export is asked of this client, and writes only the rows whose office it reaches
  // (`filasExportables`): no route on the server sees an export, so this is the only check.
  const acceso = useAccesoCierres();
  const puedeExportar = puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode });
  // B-72 — the client's worked Saturdays, under the calendar. Changing them opens or closes every
  // office's day, so it needs `cierres.calendar.manage` on the whole client — among the built-in
  // roles, only Administrador.
  const puedeEditarSabados = puede(acceso, PERMISO.ADMINISTRAR_CALENDARIO, { cliente: clientCode, entero: true });
  const [cargando, setCargando] = useState(true);
  const [todasLasFilas, setTodasLasFilas] = useState([]);
  const hoyReal = useMemo(() => new Date(), []);
  const vistaInicial = { anio: hoyReal.getFullYear(), mes: hoyReal.getMonth() };
  const [vista, setVista] = useState(vistaInicial);
  const [oficinaId, setOficinaId] = useState("todas");
  const [estado, setEstado] = useState("todos");
  const [busqueda, setBusqueda] = useState("");
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [detalleClosingId, setDetalleClosingId] = useState(null);
  // B-80 — bumped after a closing is redone from its detail, so the screen reads it again.
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    // B-55 — wait for the client list: a fetch that goes out without a `clientCode` is the 400
    // a business with two cierre clients answers with.
    if (!listo) return undefined;

    let activo = true;
    (async () => {
      setCargando(true);
      const response = await filterOffices(props.token, {}, 500, props.mantisBusiness._id, "brinks", clientCode).catch(() => null);
      if (!activo) return;
      if (response?.success) setTodasLasFilas(response.data.map(filaDeCierre));
      setCargando(false);
    })();
    return () => {
      activo = false;
    };
  }, [props.token, props.mantisBusiness._id, clientCode, listo, recarga]);

  const opcionesOficinas = useMemo(
    () =>
      [...new Map(todasLasFilas.map((f) => [f.oficinaId, { valor: f.oficinaId, etiqueta: `${f.codigo} · ${f.oficina}` }])).values()].sort(
        (a, b) => a.etiqueta.localeCompare(b.etiqueta)
      ),
    [todasLasFilas]
  );

  const mesVisible = claveMes(vista);
  const filtrosActivos = oficinaId !== "todas" || estado !== "todos" || busqueda.trim() !== "";
  const cierresVisibles = useMemo(() => {
    const termino = busqueda.trim().toLocaleLowerCase("es");
    return todasLasFilas.filter(
      (f) =>
        f.fecha.startsWith(mesVisible) &&
        (oficinaId === "todas" || f.oficinaId === oficinaId) &&
        (estado === "todos" || f.estado === estado) &&
        (!termino || f.oficina.toLocaleLowerCase("es").includes(termino) || f.codigo.toLocaleLowerCase("es").includes(termino))
    );
  }, [busqueda, estado, mesVisible, oficinaId, todasLasFilas]);

  const cierresPorFecha = useMemo(() => {
    const mapa = new Map();
    cierresVisibles.forEach((c) => mapa.set(c.fecha, [...(mapa.get(c.fecha) || []), c]));
    return mapa;
  }, [cierresVisibles]);
  const fechasActivas = useMemo(() => new Set(cierresPorFecha.keys()), [cierresPorFecha]);
  const cierresDelDia = fechaSeleccionada ? cierresPorFecha.get(fechaSeleccionada) || [] : [];
  const celdas = useMemo(() => construirMes(vista), [vista]);
  const porcentajeCuadre = cierresVisibles.length
    ? Math.round((cierresVisibles.filter((c) => c.estado === "cuadre").length / cierresVisibles.length) * 100)
    : 0;

  const seleccionarFecha = (fecha) => setFechaSeleccionada(fecha);
  const mover = (cantidad) => {
    setVista((actual) => desplazarMes(actual.anio, actual.mes, cantidad));
    setFechaSeleccionada(null);
  };
  const limpiar = () => {
    setOficinaId("todas");
    setEstado("todos");
    setBusqueda("");
  };

  const exportar = () => {
    const filas = filasExportables(acceso, clientCode, cierresVisibles, (c) => ({ oficina: c.raw?.codigo_oficina, region: c.raw?.region }));
    const header = ["Fecha", "Codigo", "Oficina", "Cuadre total", "Estado"];
    const rows = filas.map((c) => [fechaDMY(c.fecha), c.codigo, c.oficina, c.diferencia ?? "", ETIQUETA_ESTADO[c.estado]]);
    downloadCsv(`cierres-${mesVisible}.csv`, toCsv(header, rows));
  };

  return (
    <div className="brinks-scope font-redhat flex min-h-0 flex-col gap-2 bg-background p-3 sm:p-4">
      <header className="mb-1 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="font-lora text-[21px] font-semibold text-brinks-foreground">Calendario de cierres</h1>
          <p className="mt-0.5 text-[13px] text-brinks-muted-foreground">Seguimiento mensual de todas las oficinas</p>
        </div>
        {puedeExportar ? (
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={exportar}>
            <Download className="h-4 w-4 shrink-0" /> <span className="truncate">Exportar</span>
          </Button>
        ) : null}
      </header>

      {cargando ? (
        <div className="grid place-items-center rounded-[10px] border border-dashed bg-card py-14">
          <Spinner size="lg" />
        </div>
      ) : (
        <section {...guia("El mes", "Cada día lleva el color de su estado. Toca un día para ver su detalle.")} className="overflow-hidden rounded-[12px] border bg-card shadow-brinks-card">
          <div className="grid gap-2 border-b p-2 sm:grid-cols-[minmax(0,1fr)_180px_180px_auto] sm:p-2.5">
            <div className="relative min-w-0">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brinks-muted-foreground" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                {...guia("Buscar oficina", "Escribe el nombre o el código para ver el calendario de una sola oficina.")} placeholder="Buscar oficina o código"
                className="h-8 w-full rounded-[7px] border bg-background pl-8 pr-3 text-[12px] outline-none transition focus:border-brinks-border-strong"
              />
            </div>
            <Select value={oficinaId} onValueChange={setOficinaId}>
              <SelectTrigger className="h-8 bg-background text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="brinks-scope font-redhat">
                <SelectItem value="todas">Todas las oficinas</SelectItem>
                {opcionesOficinas.map((o) => (
                  <SelectItem key={o.valor} value={o.valor}>
                    {o.etiqueta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="h-8 bg-background text-[12px]">
                <SelectValue>{estado === "todos" ? "Todos los estados" : ETIQUETA_ESTADO[estado]}</SelectValue>
              </SelectTrigger>
              <SelectContent className="brinks-scope font-redhat">
                <SelectItem value="todos">Todos los estados</SelectItem>
                {ESTADOS.map((valor) => (
                  <SelectItem key={valor} value={valor}>
                    {ETIQUETA_ESTADO[valor]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filtrosActivos ? (
              <Button variant="ghost" size="sm" className="h-8 px-2.5" onClick={limpiar}>
                Limpiar
              </Button>
            ) : (
              <span />
            )}
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b px-2.5 py-2 sm:flex sm:justify-between sm:px-3.5">
            <div className="flex min-w-0 items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => mover(-1)} aria-label="Mes anterior">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => mover(1)} aria-label="Mes siguiente">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <h2 className="ml-1 truncate text-[14px] font-semibold text-brinks-foreground sm:text-[15px]">{nombreDelMes(vista)}</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-3 text-[11px]"
              onClick={() => {
                setVista(vistaInicial);
                setFechaSeleccionada(null);
              }}
            >
              Hoy
            </Button>
          </div>

          <div className="grid min-w-0 lg:grid-cols-[minmax(0,1fr)_270px] xl:grid-cols-[minmax(0,1fr)_300px]">
            <div className="min-w-0 p-1.5 sm:p-2.5">
              <div className="grid grid-cols-7 border-b text-center text-[9px] font-semibold uppercase text-brinks-muted-foreground sm:text-[10px]">
                {DIAS.map((dia) => (
                  <div key={dia} className="py-2">
                    {dia}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 overflow-hidden rounded-b-[8px] border-l border-t">
                {celdas.map((celda, indice) => {
                  const lista = celda.pertenece ? cierresPorFecha.get(celda.clave) || [] : [];
                  const seleccionada = celda.clave === fechaSeleccionada;
                  return (
                    <div
                      key={`${celda.dia}-${indice}`}
                      className={cn(
                        "min-w-0 border-b border-r bg-card p-1 transition sm:min-h-[86px] sm:p-1.5 xl:min-h-[100px]",
                        !celda.pertenece && "bg-brinks-surface/50",
                        seleccionada && "bg-brinks-surface ring-1 ring-inset ring-brinks-foreground/20"
                      )}
                    >
                      <Button
                        variant="ghost"
                        disabled={!celda.pertenece}
                        onClick={() => seleccionarFecha(celda.clave)}
                        className={cn(
                          "h-5 min-w-5 rounded-[5px] p-0 text-[10px] font-medium sm:text-[11px]",
                          !celda.pertenece && "text-brinks-muted-foreground/40",
                          seleccionada && "bg-primary text-primary-foreground"
                        )}
                      >
                        {celda.dia}
                      </Button>
                      <div className="mt-0.5 space-y-0.5">
                        {lista.slice(0, 3).map((c) => (
                          <Button
                            key={c.id}
                            variant="ghost"
                            onClick={() => c.closingId && setDetalleClosingId(c.closingId)}
                            title={`${c.oficina}: ${ETIQUETA_ESTADO[c.estado]}`}
                            className={cn(
                              "hidden h-[18px] w-full justify-start gap-1 overflow-hidden rounded-[4px] border px-1 text-[8px] font-medium sm:flex",
                              FILA_CLASES[c.estado]
                            )}
                          >
                            <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", PUNTO_CLASES[c.estado])} />
                            <span className="truncate">{c.oficina}</span>
                          </Button>
                        ))}
                        {lista.length ? (
                          <Button variant="ghost" onClick={() => seleccionarFecha(celda.clave)} className="flex h-5 w-full justify-start gap-1 overflow-hidden px-0.5 text-[8px] sm:hidden">
                            <span className="flex -space-x-0.5">
                              {ESTADOS.filter((v) => lista.some((c) => c.estado === v)).map((v) => (
                                <span key={v} className={cn("h-1.5 w-1.5 rounded-full ring-1 ring-card", PUNTO_CLASES[v])} />
                              ))}
                            </span>
                            <span>{lista.length}</span>
                          </Button>
                        ) : null}
                        {lista.length > 3 ? (
                          <Button variant="ghost" onClick={() => seleccionarFecha(celda.clave)} className="hidden h-4 w-full justify-start px-1 text-[8px] text-brinks-muted-foreground sm:flex">
                            +{lista.length - 3} más
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
              {!cierresVisibles.length ? <div className="py-6 text-center text-[12px] text-brinks-muted-foreground">No hay cierres para los filtros seleccionados.</div> : null}
            </div>

            <aside {...guia("Detalle", "Lo que pasó el día elegido y el resumen del mes.")} className="border-t lg:border-l lg:border-t-0">
              <section className="border-b p-3.5">
                <div className="mb-3 flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-brinks-muted-foreground" />
                  <h3 className="text-[12px] font-semibold text-brinks-foreground">{nombreDelMes(vista)}</h3>
                </div>
                <MiniCalendario vista={vista} fechasActivas={fechasActivas} seleccion={fechaSeleccionada} onSeleccionar={seleccionarFecha} />
              </section>

              {fechaSeleccionada ? (
                <section className="p-3.5">
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div>
                      <p className="text-[10px] uppercase text-brinks-muted-foreground">Detalle del día</p>
                      <h3 className="mt-0.5 text-[13px] font-semibold capitalize text-brinks-foreground">{fechaLegible(fechaSeleccionada)}</h3>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setFechaSeleccionada(null)} aria-label="Cerrar selección">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <ResumenEstados lista={cierresDelDia} />
                  <div className="mt-3 max-h-[240px] space-y-1 overflow-y-auto border-t pt-3">
                    {cierresDelDia.map((c) => (
                      <Button
                        key={c.id}
                        variant="ghost"
                        onClick={() => c.closingId && setDetalleClosingId(c.closingId)}
                        className="h-auto w-full justify-between gap-2 rounded-[7px] px-2 py-2 text-left"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-[11px] font-medium text-brinks-foreground">{c.oficina}</span>
                          <span className="block text-[9px] text-brinks-muted-foreground">Código {c.codigo}</span>
                        </span>
                        <span className={cn("h-2 w-2 shrink-0 rounded-full", PUNTO_CLASES[c.estado])} />
                      </Button>
                    ))}
                    {!cierresDelDia.length ? <p className="py-3 text-center text-[11px] text-brinks-muted-foreground">Sin cierres para este día.</p> : null}
                  </div>
                </section>
              ) : (
                <>
                  <section className="border-b p-3.5">
                    <h3 className="mb-2 text-[10px] font-semibold uppercase text-brinks-muted-foreground">Estados del mes</h3>
                    <ResumenEstados lista={cierresVisibles} />
                  </section>
                  <section className="p-3.5">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[9px] uppercase text-brinks-muted-foreground">Cierres</p>
                        <p className="mt-1 text-[22px] font-semibold tabular-nums text-brinks-foreground">{cierresVisibles.length}</p>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase text-brinks-muted-foreground">En cuadre</p>
                        <p className="mt-1 text-[22px] font-semibold tabular-nums text-brinks-foreground">{porcentajeCuadre}%</p>
                      </div>
                    </div>
                    <p className="mt-3 text-[10px] text-brinks-muted-foreground">Selecciona un día para revisar todas sus oficinas.</p>
                  </section>
                </>
              )}
            </aside>
          </div>
        </section>
      )}

      {/* Shown only for a client whose offices do not close on Saturdays by default (Bancamía):
          the backend says so (`usesOpenDates`), so no client is named here. */}
      <SabadosLaborales
        token={props.token}
        mantisBusinessId={props.mantisBusiness._id}
        clientCode={clientCode}
        listo={listo}
        puedeEditar={puedeEditarSabados}
        motivoSinPermiso="Solo lectura: solo un administrador puede cambiar los sábados laborales."
      />

      <CierreDetailDialog
        closingId={detalleClosingId}
        panelRow={todasLasFilas.find((f) => f.closingId === detalleClosingId)?.raw}
        token={props.token}
        mantisBusinessId={props.mantisBusiness._id}
        onOpenChange={(open) => !open && setDetalleClosingId(null)}
        onCambio={() => setRecarga((n) => n + 1)}
      />
    </div>
  );
}

export default connect(selectAuthState)(CierresCalendario);
